import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FilterDrawer from '../components/FilterDrawer';
import { getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';
import { Edit, Trash2, Plus, Calendar as CalendarIcon, MapPin, Users, Grid3x3, List, Search, SlidersHorizontal } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getListingTabs, filterListingsByTab, getSortingOptions, sortListings } from '../utils/listingTabsConfig';
import { getListingFilters, applyListingFilters } from '../utils/listingFiltersConfig';

const Events = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ selectedFilters: {}, rangeValues: {} });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await apiCall('/api/listings/?listing_type=event');
      setEvents(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load events',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await apiCall(`/api/listings/${id}`, { method: 'DELETE' });
      setEvents(events.filter((e) => e.id !== id));
      toast({
        title: 'Success',
        description: 'Event deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete event',
        variant: 'destructive'
      });
    }
  };

  // Get filtered and sorted events
  const getDisplayEvents = () => {
    let filtered = events;

    // Apply tab filter
    filtered = activeTab === 'all' ? filtered : filterListingsByTab(filtered, activeTab);

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.title?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query) ||
        e.category?.toLowerCase().includes(query)
      );
    }

    // Apply filters from drawer
    filtered = applyListingFilters(filtered, appliedFilters, 'Events');

    // Apply sorting
    return sortListings(filtered, sortBy);
  };

  const displayEvents = getDisplayEvents();
  const tabs = getListingTabs(events);
  const activeFiltersCount = Object.values(appliedFilters.selectedFilters).flat().length + 
    Object.keys(appliedFilters.rangeValues).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/listings')}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
                  title="Back to All Listings"
                  data-testid="back-to-listings-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Events</h1>
              </div>
            <p className="text-gray-600 mt-2">Manage event listings with ticket sales</p>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => navigate('/listings/events/new')} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Event
            </Button>
          </div>
        </div>

        {/* Tabs, Search, Filters, Sorting */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-4">
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <TabsList className="inline-flex md:grid md:grid-cols-6 md:w-full md:max-w-4xl gap-1 min-w-max md:min-w-0">
                  {tabs.map(tab => (
                    <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                      <tab.icon className="h-3 w-3 md:h-4 md:w-4" />
                      {tab.label} ({tab.count})
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="all" className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                    All ({events.length})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search events by title, description, category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Button 
                variant="outline" 
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {getSortingOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={getListingFilters(events, 'Events')}
          onApplyFilters={setAppliedFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-gray-500">Loading events...</div>
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No events found</p>
            <Button onClick={() => navigate('/listings/events/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first event
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayEvents.map((event) => (
              <Card key={event.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/listings/events/${event.id}`)}>
                <ListingImage
                  src={getFirstImage(event.media)}
                  alt={event.title}
                  className="w-full h-48 object-cover"
                  placeholderContent={<span className="text-gray-400">No image</span>}
                />
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{event.title}</h3>
                    <Badge variant={event.is_active ? 'default' : 'secondary'}>
                      {event.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {event.description}
                  </p>
                  <div className="space-y-2 mb-3">
                    {event.event_date && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4" />
                        {new Date(event.event_date).toLocaleDateString()}
                      </div>
                    )}
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        {event.location}
                      </div>
                    )}
                    {event.max_attendees && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        Max {event.max_attendees} attendees
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Badge variant="outline">{event.category || 'Event'}</Badge>
                      {event.data?.registrationType === 'free' && (
                        <Badge className="bg-green-600 text-white">
                          Free Event
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/events/edit/${event.id}`); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Event</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Location</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Attendees</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayEvents.map((event) => (
                    <tr key={event.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/listings/events/${event.id}`)}>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{event.title}</p>
                          {event.data?.registrationType === 'free' && (
                            <Badge className="bg-green-600 text-white text-xs mt-1">Free Event</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">{event.data?.startDate || '-'}</td>
                      <td className="p-4">{event.data?.venueName || event.data?.locationType || '-'}</td>
                      <td className="p-4">{event.data?.capacity || '-'}</td>
                      <td className="p-4">
                        <Badge variant={event.is_active ? 'default' : 'secondary'}>
                          {event.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/listings/events/edit/${event.id}`); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Events;
